import React, { useContext, useEffect, useRef, useState } from 'react'
import { Button, IconButton, TextField, ToggleButton, ToggleButtonGroup, Tooltip } from '@mui/material'
import { LoadingButton } from '@mui/lab'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import AddIcon from '@mui/icons-material/Add'
import CreateIcon from '@mui/icons-material/Create'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'

import { SessionContext } from '../../context/SessionContext'
import {
  createTestCode,
  updateTestCodeInSession,
  updateTestListInSession,
  validateTestCode,
} from '../../service/testService'
import { getSessionById } from '../../service/sessionService'
import { draftClassChecks } from '../../service/gptService'
import { showToast } from '../commonUnit/Toast'
import { editorCaseLabel, isHiddenTest, testsInStudentOrder } from '../session/testVisibility'
import '../../css/UnitTestList.scss'

const codeFont = { fontFamily: 'Monaco, Menlo, monospace', fontSize: '13px' }

// The test editor of a Python class exercise (session.test_code.test_style 'class'), shown by
// TestList in place of its input / expected-output editor, in the same card layout. Each test
// is a check - a name and a few lines of Python that use the student's class, the last line
// an expression that must be True (testResults.js) - public or hidden like any test.
//
// Same contract as TestList's editor: edits are saved on blur; "Create Unit Tests" (or the
// creation wizard's Continue, through createUnitTestsRef) compiles the checks into the
// harness and runs them against an AI reference solution of the class. Checks that fail
// against it are flagged (`suspect`) for the instructor to look at; nothing is rewritten.
const ClassCheckList = ({ hideCreateButton = false, createUnitTestsRef = null, onTestListChange = null }) => {
  const { session } = useContext(SessionContext)
  const [checks, setChecks] = useState([])
  const checksRef = useRef(checks)
  checksRef.current = checks
  const syncedFor = useRef(null)
  const [publicNum, setPublicNum] = useState(2)
  const [hiddenNum, setHiddenNum] = useState(3)
  const [drafting, setDrafting] = useState(false)
  const [compiling, setCompiling] = useState(false)

  // Re-seeded only when the session changes, not on every poll: an unsaved edit would be
  // overwritten otherwise (the rule TestList's editor follows).
  useEffect(() => {
    if (!session || syncedFor.current === session.id) return
    syncedFor.current = session.id
    setChecks(Array.isArray(session.test_list) ? session.test_list : [])
  }, [session])

  useEffect(() => {
    if (onTestListChange) onTestListChange(checks)
  }, [checks]) // eslint-disable-line react-hooks/exhaustive-deps

  if (!session) return null
  const busy = drafting || compiling

  const save = (next) => {
    setChecks(next)
    return updateTestListInSession(session.id, next)
  }
  const updateField = (index, field, value) => {
    setChecks((prev) => prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)))
  }
  const saveCurrent = () => updateTestListInSession(session.id, checksRef.current)

  const addCheck = () =>
    save([...checksRef.current, { testName: `Check ${checksRef.current.length + 1}`, check: '', hidden: false }])

  const draft = async () => {
    const wanted = [
      { hidden: false, count: Number(publicNum) },
      { hidden: true, count: Number(hiddenNum) },
    ].filter((kind) => Number.isInteger(kind.count) && kind.count > 0)
    if (!wanted.length) {
      showToast('Enter how many public and hidden checks to draft (at least one in total).', 'warning')
      return
    }
    setDrafting(true)
    const added = []
    try {
      const fresh = await getSessionById(session.id).catch(() => null)
      const template = fresh?.test_code?.template ?? session.test_code?.template ?? ''
      // One kind after the other, so the second request knows the first one's checks.
      for (const kind of wanted) {
        const existing = [...checksRef.current, ...added].map((row) => row.check)
        const drafted = await draftClassChecks({ task: session.task, template, count: kind.count, existing })
        added.push(...drafted.map((row) => ({ ...row, hidden: kind.hidden })))
      }
      if (!added.length) {
        showToast('The AI did not suggest any checks. Add them by hand, or try again.', 'warning')
        return
      }
      await save([...checksRef.current, ...added])
    } catch (error) {
      console.error('Drafting checks failed:', error)
      if (added.length) {
        // Keep what the first request drafted.
        await save([...checksRef.current, ...added])
        showToast('Only some of the checks could be drafted. Draft again for the rest, or add them by hand.', 'warning')
      } else {
        showToast('Could not draft checks right now. Add them by hand, or try again in a moment.', 'error')
      }
    } finally {
      setDrafting(false)
    }
  }

  // `blocked`: why nothing was compiled, for the caller to show.
  const compile = async () => {
    setCompiling(true)
    try {
      const rows = checksRef.current
      if (!rows.length) {
        showToast('Add at least one check', 'warning')
        return { flagged: 0, total: 0, allPassed: false }
      }
      // An empty check fails for every student (there is no expression to be True).
      const blank = rows.findIndex((row) => !String(row.check ?? '').trim())
      if (blank !== -1) {
        const label = editorCaseLabel(rows, blank)
        return { flagged: 0, total: rows.length, allPassed: false, blocked: `${label} has no code yet. Write the check, or delete it.` }
      }
      const code = await createTestCode(rows, session.task, 'Python', { style: 'class' })
      // createTestCode leaves the student template empty: keep the saved one.
      const fresh = await getSessionById(session.id).catch(() => null)
      code.template = fresh?.test_code?.template ?? session.test_code?.template ?? ''
      if (!(await updateTestCodeInSession(session.id, code))) throw new Error('Could not save the harness')

      let result = null
      try {
        result = (await validateTestCode(session.task, code, 'python-class', session.id))?.result ?? null
      } catch (error) {
        console.error('Checking against a reference solution failed:', error)
      }
      if (!Array.isArray(result)) {
        showToast('The checks are saved, but could not be tried against an AI reference solution.', 'warning', 5000)
      }
      const marked = rows.map((row, index) => {
        const { suspect, ...rest } = row
        return Array.isArray(result) && result[index] !== true ? { ...rest, suspect: true } : rest
      })
      await save(marked)
      const flagged = marked.filter((row) => row.suspect).length
      return { flagged, total: marked.length, allPassed: Array.isArray(result) && flagged === 0 }
    } finally {
      setCompiling(false)
    }
  }

  // The button's own run (the wizard words its own messages from compile's result).
  const compileFromButton = async () => {
    try {
      const { flagged, total, allPassed, blocked } = await compile()
      if (blocked) showToast(blocked, 'warning')
      else if (allPassed) showToast(`Unit tests created. All ${total} checks pass against the AI's reference class.`, 'success')
      else if (flagged) {
        showToast(`Unit tests created. ${flagged} of ${total} checks did not pass against the AI's reference class.`, 'warning', 5000)
      }
    } catch (error) {
      console.error('Creating the unit tests failed:', error)
      showToast('Could not create the unit tests. Check your connection, then try again.', 'error')
    }
  }

  if (createUnitTestsRef) {
    createUnitTestsRef.current = {
      run: compile,
      testCount: checks.length,
      testList: checks,
      loading: compiling,
      generating: drafting,
    }
  }

  return (
    <div className="tl-compact" aria-busy={busy}>
      <div className="tl-compact__gen">
        <div className="tl-compact__toolbar">
          {[
            { kind: 'Public', count: publicNum, setCount: setPublicNum },
            { kind: 'Hidden', count: hiddenNum, setCount: setHiddenNum },
          ].map((row) => (
            <TextField
              key={row.kind}
              className="tl-compact__count"
              size="small"
              label={`${row.kind} checks`}
              type="number"
              value={row.count}
              disabled={busy}
              inputProps={{ min: 0 }}
              onChange={(e) => row.setCount(e.target.value)}
            />
          ))}
          <LoadingButton
            className="tl-compact__generate"
            variant="outlined"
            loadingPosition="start"
            startIcon={<AutoAwesomeIcon />}
            loading={drafting}
            disabled={compiling}
            onClick={draft}>
            Draft with AI
          </LoadingButton>
        </div>
        <div className="tl-compact__hint">
          Each check is a few lines of Python that use the class and end with one expression that must be True,
          e.g. <code>t = TaskList()</code> then <code>t.count() == 0</code>. Use only the methods the description
          names. Students see a public check&apos;s name and code; a hidden check shows only its name and whether it
          passed.
        </div>
      </div>

      {checks.length === 0 && (
        <div className="tl-compact__empty">No checks yet. Draft some with AI, or add one by hand.</div>
      )}

      {testsInStudentOrder(checks).map(({ test, index }) => (
        <div key={index} className={`tl-case${test.suspect ? ' tl-case--flagged' : ''}`}>
          <div className="tl-case__head">
            <span className="tl-case__title">{editorCaseLabel(checks, index)}</span>
            <ToggleButtonGroup
              className="tl-case__visibility"
              size="small"
              exclusive
              value={isHiddenTest(test) ? 'hidden' : 'public'}
              disabled={busy}
              onChange={(_e, next) =>
                next && save(checksRef.current.map((row, i) => (i === index ? { ...row, hidden: next === 'hidden' } : row)))
              }
              aria-label={`${editorCaseLabel(checks, index)}: public or hidden`}>
              <ToggleButton value="public">Public</ToggleButton>
              <ToggleButton value="hidden">Hidden</ToggleButton>
            </ToggleButtonGroup>
            <Tooltip title="Delete this check">
              <IconButton
                size="small"
                aria-label={`Delete ${editorCaseLabel(checks, index)}`}
                disabled={busy}
                onClick={() => save(checksRef.current.filter((_row, i) => i !== index))}>
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </div>
          <TextField
            size="small"
            fullWidth
            label="Name"
            value={test.testName ?? ''}
            disabled={busy}
            onChange={(e) => updateField(index, 'testName', e.target.value)}
            onBlur={saveCurrent}
          />
          <TextField
            size="small"
            fullWidth
            multiline
            minRows={2}
            maxRows={10}
            label="Check (last line must be True)"
            value={test.check ?? ''}
            disabled={busy}
            inputProps={{ spellCheck: false, style: codeFont }}
            onChange={(e) => updateField(index, 'check', e.target.value)}
            onBlur={saveCurrent}
          />
          {test.suspect && (
            <div className="tl-case__suggestion">
              <div>
                This check did not pass against the AI&apos;s reference solution. Make sure it uses the class as the
                description says, and that its last line is True for a correct class.
              </div>
            </div>
          )}
        </div>
      ))}

      <Button className="tl-compact__add" startIcon={<AddIcon />} disabled={busy} onClick={addCheck}>
        Add check
      </Button>

      {!hideCreateButton && (
        <LoadingButton
          variant="outlined"
          loadingPosition="start"
          startIcon={<CreateIcon />}
          loading={compiling}
          disabled={drafting}
          onClick={compileFromButton}>
          Create Unit Tests
        </LoadingButton>
      )}
    </div>
  )
}

export default ClassCheckList
